import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useTaskStatuses, STATUS_FALLBACK } from "@/lib/useTaskStatuses";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ListTodo, Plus, Trash2, GanttChartSquare, ListChecks, Calendar as CalendarIcon } from "lucide-react";

const STATUS_META = STATUS_FALLBACK;  // kept as a bare fallback for old callers
const PRIORITY_META = { low: "#94A3B8", medium: "#FBBF24", high: "#F87171" };

/**
 * TasksTimeline — a compact project management panel for a single deal.
 * Shows a task list + a Gantt-lite timeline (horizontal bars on a shared date axis).
 */
export default function TasksTimeline({ dealId, teamMembers = [] }) {
  const qc = useQueryClient();
  const { meta: statusMeta, list: statusList } = useTaskStatuses();
  const [view, setView] = useState("list");  // list | timeline
  const [showNew, setShowNew] = useState(false);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["deal-tasks", dealId],
    queryFn: () => api.get(`/deals/${dealId}/tasks`).then((r) => r.data),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/tasks/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-tasks", dealId] });
      // A task moving to a terminal status can auto-close the deal + freeze the
      // cost summary → refresh so the confetti hook + banner fires.
      qc.invalidateQueries({ queryKey: ["cost-summary", dealId] });
    },
    onError: () => toast.error("Task update failed"),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/tasks/${id}`),
    onSuccess: () => {
      toast.success("Task removed");
      qc.invalidateQueries({ queryKey: ["deal-tasks", dealId] });
      qc.invalidateQueries({ queryKey: ["cost-summary", dealId] });
    },
  });

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="tasks-timeline">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-primary" />
          <div className="font-display text-xl font-extrabold">Project Tasks</div>
          <span className="text-xs text-muted-foreground">· {tasks.filter((t) => t.status === "done").length}/{tasks.length} done</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-sm bg-secondary/40 border border-border p-0.5 flex">
            <button onClick={() => setView("list")} className={`px-2.5 py-1.5 rounded-sm text-xs transition-colors ${view === "list" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} data-testid="tasks-view-list"><ListChecks className="h-3.5 w-3.5" /></button>
            <button onClick={() => setView("timeline")} className={`px-2.5 py-1.5 rounded-sm text-xs transition-colors ${view === "timeline" ? "bg-primary/20 text-primary" : "text-muted-foreground"}`} data-testid="tasks-view-timeline"><GanttChartSquare className="h-3.5 w-3.5" /></button>
          </div>
          <Button size="sm" className="rounded-sm bg-primary" onClick={() => setShowNew(true)} data-testid="task-new-btn"><Plus className="h-3.5 w-3.5 mr-1" />New task</Button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!isLoading && tasks.length === 0 && (
        <div className="text-center py-10 text-sm text-muted-foreground">
          No tasks yet. Click <span className="text-white font-semibold">New task</span> to start planning the deliverables.
        </div>
      )}

      {view === "list" && tasks.length > 0 && (
        <TaskList tasks={tasks} team={teamMembers} statusMeta={statusMeta} statusList={statusList} onPatch={(id, body) => patchMut.mutate({ id, body })} onDelete={(id) => delMut.mutate(id)} />
      )}
      {view === "timeline" && tasks.length > 0 && (
        <Gantt tasks={tasks} team={teamMembers} statusMeta={statusMeta} statusList={statusList} onPatch={(id, body) => patchMut.mutate({ id, body })} />
      )}

      {showNew && <NewTaskDialog dealId={dealId} team={teamMembers} onClose={() => setShowNew(false)} onCreated={() => qc.invalidateQueries({ queryKey: ["deal-tasks", dealId] })} />}
    </Card>
  );
}

function TaskList({ tasks, team, statusMeta, statusList, onPatch, onDelete }) {
  return (
    <div className="space-y-1">
      {tasks.map((t) => {
        const meta = statusMeta[t.status] || statusMeta.todo;
        const isTerminal = meta.is_terminal || t.status === "done" || t.status === "cancelled";
        const overdue = t.due_date && !isTerminal && new Date(t.due_date) < new Date();
        // Fallback: when the current status has no first-class checkbox mapping,
        // toggling the checkbox flips todo ↔ (first terminal status in the list).
        const firstTerminal = (statusList.find((s) => s.is_terminal) || { key: "done" }).key;
        return (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center gap-3 p-2.5 rounded-sm hover:bg-secondary/40 group transition-colors">
            <Checkbox
              checked={isTerminal}
              onCheckedChange={(v) => onPatch(t.id, { status: v ? firstTerminal : "todo" })}
              data-testid={`task-check-${t.id}`}
              className="flex-none"
            />
            <div className="flex-1 min-w-0">
              <div className={`text-sm truncate ${isTerminal ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                {t.priority && t.priority !== "medium" && (
                  <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: PRIORITY_META[t.priority] }}>{t.priority}</span>
                )}
                {t.due_date && (
                  <span className={`text-[10px] flex items-center gap-1 ${overdue ? "text-red-400" : "text-muted-foreground"}`}>
                    <CalendarIcon className="h-3 w-3" />
                    {new Date(t.due_date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                    {overdue ? " · overdue" : ""}
                  </span>
                )}
                {t.assignee?.name && <span className="text-[10px] text-muted-foreground">· {t.assignee.name}</span>}
              </div>
            </div>
            <Select value={t.status} onValueChange={(v) => onPatch(t.id, { status: v })}>
              <SelectTrigger
                className="h-7 rounded-sm text-[11px] w-32 flex-none border-0 hover:opacity-80 transition-opacity"
                style={{ backgroundColor: meta.bg, color: meta.color }}
                data-testid={`task-status-${t.id}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusList.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`task-status-option-${t.id}-${s.key}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full inline-block" style={{ background: s.color }} />
                      {s.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={t.assigned_to || ""} onValueChange={(v) => onPatch(t.id, { assigned_to: v || null })}>
              <SelectTrigger className="h-7 rounded-sm bg-secondary/40 text-xs w-32 flex-none" data-testid={`task-assign-${t.id}`}><SelectValue placeholder="Assign…" /></SelectTrigger>
              <SelectContent>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => { if (window.confirm("Delete this task?")) onDelete(t.id); }} data-testid={`task-delete-${t.id}`} className="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function Gantt({ tasks, team, statusMeta, statusList, onPatch }) {
  const withDates = tasks.filter((t) => t.start_date || t.due_date);
  const { minDate, maxDate, days } = useMemo(() => {
    if (withDates.length === 0) return { minDate: null, maxDate: null, days: 0 };
    const dates = withDates.flatMap((t) => [t.start_date, t.due_date].filter(Boolean)).map((d) => new Date(d));
    const mn = new Date(Math.min(...dates.map((d) => d.getTime())));
    const mx = new Date(Math.max(...dates.map((d) => d.getTime())));
    mn.setDate(mn.getDate() - 2);
    mx.setDate(mx.getDate() + 2);
    const daysCount = Math.max(1, Math.ceil((mx - mn) / 86400000));
    return { minDate: mn, maxDate: mx, days: daysCount };
  }, [withDates]);

  if (withDates.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-6">Add start/due dates on tasks to see the timeline.</div>;
  }

  const dayHeaders = [];
  for (let i = 0; i <= days; i += Math.max(1, Math.round(days / 8))) {
    const d = new Date(minDate); d.setDate(d.getDate() + i);
    dayHeaders.push({ pct: (i / days) * 100, label: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) });
  }

  return (
    <div className="space-y-2" data-testid="tasks-gantt">
      <div className="relative h-6 border-b border-border">
        {dayHeaders.map((h, i) => (
          <div key={i} className="absolute text-[10px] uppercase tracking-widest text-muted-foreground -translate-x-1/2" style={{ left: `${h.pct}%` }}>{h.label}</div>
        ))}
      </div>
      {tasks.map((t) => {
        const meta = statusMeta[t.status] || statusMeta.todo;
        const isTerminal = meta.is_terminal || t.status === "done" || t.status === "cancelled";
        const s = t.start_date ? new Date(t.start_date) : (t.due_date ? new Date(t.due_date) : null);
        const e = t.due_date ? new Date(t.due_date) : (t.start_date ? new Date(t.start_date) : null);
        const left = s ? Math.max(0, ((s - minDate) / 86400000 / days) * 100) : 0;
        const width = (s && e) ? Math.max(2, ((e - s) / 86400000 / days) * 100) : 2;
        const hasDates = !!s && !!e;
        return (
          <div key={t.id} className="flex items-center gap-2" data-testid={`gantt-row-${t.id}`}>
            <div className="w-44 text-xs truncate flex-none flex items-center gap-2">
              <Select value={t.status} onValueChange={(v) => onPatch(t.id, { status: v })}>
                <SelectTrigger
                  className="rounded-sm h-5 w-5 p-0 border-0 flex items-center justify-center flex-none"
                  style={{ backgroundColor: meta.bg, color: meta.color }}
                  aria-label="Change status"
                  data-testid={`gantt-task-status-${t.id}`}
                >
                  <span className="h-2 w-2 rounded-full inline-block" style={{ background: meta.color }} />
                </SelectTrigger>
                <SelectContent>
                  {statusList.map((opt) => (
                    <SelectItem key={opt.key} value={opt.key} data-testid={`gantt-task-status-option-${t.id}-${opt.key}`}>
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full inline-block" style={{ background: opt.color }} />
                        {opt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className={isTerminal ? "line-through text-muted-foreground" : ""}>{t.title}</span>
            </div>
            <div className="flex-1 h-6 relative bg-secondary/30 rounded-sm">
              {hasDates ? (
                <div
                  className="absolute top-0 h-full rounded-sm flex items-center px-2 text-[10px] font-semibold overflow-hidden"
                  style={{ left: `${left}%`, width: `${width}%`, backgroundColor: meta.bg, borderLeft: `2px solid ${meta.color}`, color: meta.color }}
                  title={`${t.start_date || "?"} → ${t.due_date || "?"}`}
                >
                  {t.assignee?.name || meta.label}
                </div>
              ) : (
                <div className="absolute top-1/2 h-2 w-2 rounded-full -translate-y-1/2" style={{ left: `${left}%`, backgroundColor: meta.color }} title="No end date set" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function NewTaskDialog({ dealId, team, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", status: "todo", priority: "medium", start_date: "", due_date: "", assigned_to: "" });
  const mut = useMutation({
    mutationFn: () => api.post(`/deals/${dealId}/tasks`, { ...form, assigned_to: form.assigned_to || null, start_date: form.start_date || null, due_date: form.due_date || null }),
    onSuccess: () => { toast.success("Task created"); onCreated(); onClose(); },
    onError: () => toast.error("Couldn't create task"),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle>New Task</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input data-testid="new-task-title" className="mt-1.5 rounded-sm bg-secondary/40" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} autoFocus /></div>
          <div><Label>Description</Label><Textarea className="mt-1.5 rounded-sm bg-secondary/40" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input data-testid="new-task-start" type="date" className="mt-1.5 rounded-sm bg-secondary/40" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Due date</Label><Input data-testid="new-task-due" type="date" className="mt-1.5 rounded-sm bg-secondary/40" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger data-testid="new-task-assign" className="mt-1.5 rounded-sm bg-secondary/40"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="new-task-submit" className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={!form.title || mut.isPending}>Create task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
